import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  User as UserIcon,
  Mail,
  Phone,
  Lock,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Save,
} from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import {
  getUserProfile,
  updateUserProfile,
  ApiError,
  type UserProfile,
} from "@/lib/api";
import { formatDate } from "@/lib/format";

// ---------------------------------------------------------------------------

export function ProfilePage() {
  const qc = useQueryClient();
  const profileQuery = useQuery<UserProfile>({
    queryKey: ["profile"],
    queryFn: getUserProfile,
  });

  const profile = profileQuery.data ?? null;

  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!profile) return;
    setForm({
      firstname: profile.firstname ?? "",
      lastname: profile.lastname ?? "",
      email: profile.email ?? "",
      phone_number: profile.phone_number ?? "",
    });
  }, [profile]);

  useEffect(() => {
    if (!message) return;
    const id = setTimeout(() => setMessage(null), 5_000);
    return () => clearTimeout(id);
  }, [message]);

  const hasChanges = useMemo(() => {
    if (!profile) return false;
    return (
      form.firstname !== (profile.firstname ?? "") ||
      form.lastname !== (profile.lastname ?? "") ||
      form.email !== (profile.email ?? "") ||
      form.phone_number !== (profile.phone_number ?? "")
    );
  }, [profile, form]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    setSaving(true);
    setMessage(null);
    try {
      await updateUserProfile(form);
      setMessage({ type: "success", text: "Profil mis à jour." });
      qc.invalidateQueries({ queryKey: ["profile"] });
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof ApiError ? err.detail || err.title : "Erreur lors de la mise à jour",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Mon profil"
        breadcrumbs={[{ label: "Mon espace" }, { label: "Profil" }]}
      />

      {profileQuery.isLoading ? (
        <div className="card card-body text-slate-500">Chargement…</div>
      ) : profileQuery.isError || !profile ? (
        <div className="card card-body text-danger-700 flex items-center gap-2">
          <AlertTriangle size={14} />
          Impossible de charger votre profil.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 card">
            <div className="card-header">
              <div>
                <div className="card-title">Informations personnelles</div>
                <div className="card-subtitle">
                  Ces informations apparaissent sur les documents et communications envoyés au
                  support.
                </div>
              </div>
            </div>
            <form onSubmit={submit} className="card-body space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Prénom" icon={<UserIcon size={14} />}>
                  <input
                    type="text"
                    value={form.firstname}
                    onChange={(e) => setForm((f) => ({ ...f, firstname: e.target.value }))}
                    className="form-input pl-9 py-2 text-sm w-full"
                    required
                  />
                </Field>
                <Field label="Nom" icon={<UserIcon size={14} />}>
                  <input
                    type="text"
                    value={form.lastname}
                    onChange={(e) => setForm((f) => ({ ...f, lastname: e.target.value }))}
                    className="form-input pl-9 py-2 text-sm w-full"
                    required
                  />
                </Field>
              </div>

              <Field label="Email" icon={<Mail size={14} />}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="form-input pl-9 py-2 text-sm w-full"
                  required
                />
              </Field>

              <Field label="Téléphone" icon={<Phone size={14} />}>
                <input
                  type="tel"
                  value={form.phone_number}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      phone_number: e.target.value.replace(/\s/g, ""),
                    }))
                  }
                  className="form-input pl-9 py-2 text-sm w-full font-mono"
                  maxLength={20}
                />
              </Field>

              {message && (
                <div
                  className={`rounded-lg border p-3 text-xs flex items-start gap-2 ${
                    message.type === "success"
                      ? "border-success-100 bg-success-50 text-success-700"
                      : "border-danger-100 bg-danger-50 text-danger-700"
                  }`}
                >
                  {message.type === "success" ? (
                    <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  )}
                  <div>{message.text}</div>
                </div>
              )}

              <button
                type="submit"
                disabled={saving || !hasChanges}
                className="btn btn-primary"
              >
                <Save size={14} />
                {saving ? "Enregistrement…" : "Enregistrer les modifications"}
              </button>
            </form>
          </div>

          <div className="space-y-5">
            {/* Read-only meta */}
            <div className="card">
              <div className="card-body">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">
                  Compte
                </div>
                <ul className="text-sm space-y-2">
                  <MetaRow label="Nom d'utilisateur" value={profile.username} mono />
                  <MetaRow label="Rôle" value={profile.role} capitalize />
                  <MetaRow
                    label="ID marchand"
                    value={profile.merchant_id != null ? `#${profile.merchant_id}` : "—"}
                    mono
                  />
                  {profile.created_at && (
                    <MetaRow label="Créé le" value={formatDate(profile.created_at)} />
                  )}
                  {profile.updated_at && (
                    <MetaRow
                      label="Dernière mise à jour"
                      value={formatDate(profile.updated_at)}
                    />
                  )}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck size={16} className="text-brand-500" />
                  <div className="font-semibold text-[#181c32] text-sm">Sécurité</div>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Changez votre mot de passe régulièrement pour protéger votre compte.
                </p>
                <Link to="/change-password" className="btn btn-light btn-sm w-full">
                  <Lock size={14} />
                  Changer le mot de passe
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Field({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-bold mb-1.5">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          {icon}
        </div>
        {children}
      </div>
    </div>
  );
}

function MetaRow({
  label,
  value,
  mono,
  capitalize,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  capitalize?: boolean;
}) {
  return (
    <li className="flex justify-between items-center gap-2">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-bold shrink-0">
        {label}
      </span>
      <span
        className={`text-right text-[#181c32] truncate ${mono ? "font-mono text-xs" : "text-sm"} ${
          capitalize ? "capitalize" : ""
        }`}
      >
        {value}
      </span>
    </li>
  );
}
